import { UploadService, BbFile } from './../../../services/upload.service';
import { Component, Input, Output, ViewChild, ElementRef, SimpleChanges, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { OverlayService } from '../../../services/overlay.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { SapiService } from '../../../services/sapi.service';
import { Embargo } from '../bb-embargo/bb-embargo.component';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { Observable } from 'rxjs';


@Component({
	selector: 'bb-upload',
	templateUrl: './bb-upload.component.html',
	styleUrls: ['./bb-upload.component.scss'],
	host: {
		'[style.pointer-events]': `dropZoneOnly ? 'none' : 'all'`
	}
})

export class BbUploadComponent implements OnInit, OnChanges {
	@Input() large: boolean;
	@Input() inline: boolean;
	@Input() dropText: string;
	@Input() dropTextRecommendation: string;
	@Input() showUploadIcon = false;
	@Input() uploadText: string;
	@Input() hideDropText: boolean;
	@Input() dropZoneOnly: boolean;
	@Input() multi = true;
	@Input() createNewMediaclip = false;
	@Input() mediaclipDefaults: Mediaclip;
	@Input() removeMediaclipOnCancel = true;
	@Input() mediaclip: Mediaclip;
	@Input() autoName: boolean;
	@Input() autoPublish: boolean;
	@Input() selectedProfile: string;
	@Input() embargo: Embargo;
	@Input() disabled: boolean;
	@Input() publishCreatives = false;
	@Input() recommendationsTable = false;
	@Input() theme: '' | 'light-v2' = '';
	// customData will be added as a prop to the uploaded File
	@Input() customData?: { [prop: string]: any };
	@Input() validateBeforeUpload: () => Observable<boolean>;

	@Output() mediaclipCreated: EventEmitter<Mediaclip>;
	@Output() bbFileCreated: EventEmitter<BbFile>;

	@Input() onFilter: (file: File) => boolean;
	@Input() mediaTypes: string[];
	@Input() useType: 'commercial' | 'editorial';
	@ViewChild('button', { static: true }) button: ElementRef;

	// TODO: implement pending uploads
	public pendingUploads: boolean;

	putRight = false;
	rippleStyle = {
		opacity: 0,
		left: '',
		top: ''
	};

	awsOptions: any;
	uploadEmbargo: Embargo;
	transcodingProfile: string;
	showRecommendations: boolean;

	constructor(private translate: TranslateService,
		private overlayService: OverlayService,
		private sapiService: SapiService,
		private uploadService: UploadService,
		private authenticationService: AuthenticationService) {
		this.mediaclipCreated = new EventEmitter<Mediaclip>();
		this.bbFileCreated = new EventEmitter<BbFile>();
		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		if (typeof this.hideDropText === 'undefined') {
			this.hideDropText = false;
		}
		this.showRecommendations = false;
	}

	ngOnInit() {
		if (!this.dropZoneOnly) {
			this.button.nativeElement.addEventListener('change', (event: any) => {
				for (let i = 0; i < this.button.nativeElement.files.length; i++) {
					this.handleFile(this.button.nativeElement.files[i]);
				}
				// Selecting the same file twice works by resetting the button value
				this.button.nativeElement.value = '';
			});
		}

		if (this.mediaclip && (isNaN(Number(this.mediaclip.id)) || Number(this.mediaclip.id) < 1)) {
			this.createNewMediaclip = true;
			this.mediaclip = null;
		}
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges.autoPublish) {
			this.awsOptions = {
				autoPublish: this.autoPublish
			};
		}
		if (simpleChanges.embargo) {
			this.uploadEmbargo = this.embargo;
		}
		if (simpleChanges.selectedProfile) {
			this.transcodingProfile = this.selectedProfile;
		}
	}

	onDropEvent(ev: any) { // DragEvent not available in Safari
		if (!this.disabled) {
			const rippleRadius = 20;
			this.rippleStyle.left = (ev.offsetX - rippleRadius) + 'px';
			this.rippleStyle.top = (ev.offsetY - rippleRadius) + 'px';
			this.rippleStyle.opacity = 1;
			setTimeout(() => {
				this.rippleStyle.opacity = 0;
			}, 2200);
		}
	}

	onFilesDropped(files: File[]) {
		if (!this.disabled) {
			for (let i = 0; i < files.length; i++) {
				this.handleFile(files[i]);
			}
			// Selecting the same file twice works by resetting the button value
			this.button.nativeElement.value = '';
		}
	}

	handleFile(file: File) {
		if (this.onFilter) {
			// for now we only support sync / boolean filter functions
			if (!this.onFilter(file)) {
				const errorMessage = this.translate.instant('INVALID_FILE_TYPE');
				this.overlayService.createToast(errorMessage);
				return;
			}
		} else if (this.mediaTypes) {
			// For now only works for video/audio and image filtering not based on file extension
			let rejectFile = true;
			this.mediaTypes.forEach((mediatype: string) => {
				if (file.type.startsWith(mediatype + '/')) {
					rejectFile = false;
					return;
				}
			});
			if (rejectFile) {
				const errorMessage = this.translate.instant('INVALID_FILE_TYPE');
				this.overlayService.createToast(errorMessage);
				return;
			}
		}

		if (!this.validateBeforeUpload) {
			this.processFile(file);
			return;
		}

		this.validateBeforeUpload().subscribe((result: boolean) => {
			if (result) {
				this.processFile(file);
			}
		});
	}

	processFile(file: File) {
		let newMediaClip: Mediaclip = {};
		let subscription: Observable<BbFile>;

		if (this.createNewMediaclip || this.mediaclip) {
			const mediaType = this.uploadService.getMediatypeForFile(file);
			if (this.mediaclipDefaults) {
				newMediaClip = { ...this.mediaclipDefaults };
			}
			newMediaClip.originalfilename = file.name;
			if (!this.mediaclipDefaults || !this.mediaclipDefaults.mediatype) {
				newMediaClip.mediatype = mediaType;
			}

			if (this.useType) {
				newMediaClip.usetype = this.useType;
			}

			if (this.autoName) {
				const autoName = file.name.substr(0, file.name.lastIndexOf('.')) || file.name;
				newMediaClip.title = autoName;
			}

			if (this.uploadEmbargo) {
				newMediaClip.embargoFrom = this.uploadEmbargo.embargoFrom;
				newMediaClip.embargoTo = this.uploadEmbargo.embargoTo;
			}

			if (!this.removeMediaclipOnCancel) {
				(file as any).removeMediaclipOnCancel = false;
			}

			if (this.transcodingProfile) {
				newMediaClip.transcodingProfile = this.transcodingProfile;
			}

			if (this.publishCreatives && newMediaClip.mediatype !== 'video') {
				if (!newMediaClip.usetype) {
					newMediaClip.usetype = 'commercial';
				}
				if (newMediaClip.usetype === 'commercial') {
					newMediaClip.status = 'published';
				}
			}

			if (this.createNewMediaclip) {
				this.sapiService.create('mediaclip', newMediaClip).subscribe((mediaclip: Mediaclip) => {
					const uploadSubscription = this.uploadService.handleFile(file, mediaclip, this.awsOptions).subscribe((bbFile: BbFile) => {
						this.bbFileCreated.emit(bbFile);
						uploadSubscription.unsubscribe();
					});
					this.mediaclipCreated.emit(mediaclip);
				});
			} else {
				subscription = this.uploadService.handleFile(file, this.mediaclip, this.awsOptions, this.customData);
			}
		} else {
			subscription = this.uploadService.handleFile(file, null, null, this.customData);
		}
		if (subscription) {
			const observable = subscription.subscribe((bbFile: BbFile) => {
				this.bbFileCreated.emit(bbFile);
				observable.unsubscribe();
			});
		}
	}
}
